<template>
  <div class="section">
    <div class="section-tit">
      전체상품<span class="sm">({{ prodValue?.total }}개)</span>
    </div>
    <ul class="product-list">
      <li v-for="(item, index) in products" :key="index" class="product-list--item">
        <router-link :to="`/productDetail/${item.goods_code}`">
          <div class="img-wrap">
            <img :src="item.main_image_url" alt="상품 이미지" @error="replaceWithDefaultImg" />

            <div v-if="item.status?.status === 'passed'" class="dim">
              <span class="text">판매종료</span>
            </div>

            <div v-else-if="item.status?.status === 'before'" class="dim">
              <span class="text">공구 오픈 예정</span>
              <span class="text orange" v-html="formatOpenDateTime(item)"></span>
            </div>
            <div v-else-if="item.is_soldOut === 'Y'" class="dim">
              <span class="text">품절</span>
            </div>
          </div>

          <div class="top">
            <span class="brand">{{ item.brand }}</span>
            <span class="name">{{ item.product_name }}</span>
          </div>

          <div class="bottom">
            <span v-if="item.status?.status === 'ing'" class="badge orange"> 진행중 </span>
            <span v-else-if="item.status?.status === 'passed'" class="badge grey"> 판매 종료 </span>
            <span v-else-if="item.status?.status === 'default'" class="badge"> 일반 판매가 </span>
            <span v-if="item.status?.status !== 'passed'" class="strike">
              {{ _formatAmount(item.cost_price) }}원
            </span>
            <span v-if="['ing', 'default'].includes(item.status?.status)" class="price">
              {{
                _formatAmount(
                  isGonggu && !_getIsPassed(item.var2, item.var3, item.open_time)
                    ? item.selling_price
                    : item.retail_price,
                )
              }}원
            </span>

            <span v-if="item.status?.status === 'before'" class="grey"> 오픈 예정 </span>
          </div>
        </router-link>
      </li>
    </ul>
  </div>

  <!-- [추가] 페이지네이션 영역 -->
  <div class="pagination" v-if="totalPages > 1">
    <button class="page-control first" :disabled="currentPage === 1" @click="goFirst">
      <img src="@/assets/images/arrow_double_left.svg" alt="처음" />
    </button>
    <button class="page-control prev" :disabled="currentPage === 1" @click="goPrev">
      <img src="@/assets/images/arrow_left.svg" alt="이전" />
    </button>

    <div class="page-numbers">
      <button
        v-for="n in pageNumbers"
        :key="n"
        class="page-num"
        :class="{ active: n === currentPage }"
        @click="goToPage(n)"
      >
        {{ n }}
      </button>
    </div>

    <button class="page-control next" :disabled="currentPage === totalPages" @click="goNext">
      <img src="@/assets/images/arrow_right.svg" alt="다음" />
    </button>
    <button class="page-control last" :disabled="currentPage === totalPages" @click="goLast">
      <img src="@/assets/images/arrow_double_right.svg" alt="끝" />
    </button>
  </div>
</template>

<script setup>
import img from '@/assets/images/Img.svg';
import { ref, watch, reactive, computed, onMounted, onBeforeUnmount } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import axios from 'axios';
import {
  _formatAmount,
  _setStorageWithExpiry,
  _getStorageWithExpiry,
  _getIsGonggu,
  _safeAtob,
  _getIsPassed,
  _getPeriodStatus,
} from '../utils/common.js';
import api from '@/utils/api.js';
import Footer from '@/components/Footer.vue';
import { isEmpty } from 'lodash';
import Header from '@/components/Header.vue';

const router = useRouter();
const isGonggu = ref(true);
const idntCode = ref(_safeAtob(_getStorageWithExpiry('id')));

const products = ref([]);
const prodValue = ref([]);

const rowsPerPage = 20; // 필요하면 10/50 등으로 조절
const currentPage = ref(1);
const totalPages = ref(1);
const route = useRoute();

const pageNumbers = computed(() => {
  const maxVisible = 5;
  const half = Math.floor(maxVisible / 2);

  let start = Math.max(1, currentPage.value - half);
  let end = Math.min(totalPages.value, start + maxVisible - 1);

  // 끝쪽에 붙으면 start를 다시 당김
  start = Math.max(1, end - maxVisible + 1);

  const pages = [];
  for (let i = start; i <= end; i += 1) pages.push(i);
  return pages;
});

onMounted(() => {
  const queryId = route.query?.id;
  const idStr = Array.isArray(queryId) ? queryId[0] : queryId;

  if (idStr) {
    _setStorageWithExpiry('sId', String(idStr));
  }
  postData(1);
});

function goToPage(page) {
  if (page < 1 || page > totalPages.value || page === currentPage.value) return;
  currentPage.value = page;
  postData(page);

  window.scrollTo({
    top: 0,
    behavior: 'smooth',
  });
}
function goFirst() {
  goToPage(1);
}
function goPrev() {
  goToPage(currentPage.value - 1);
}
function goNext() {
  goToPage(currentPage.value + 1);
}
function goLast() {
  goToPage(totalPages.value);
}

function replaceWithDefaultImg(e) {
  e.target.onerror = null;
  e.target.src = img;
}

function formatOpenDateTime(item) {
  const dateStr = (item?.var2 || '').toString().trim(); // YYYY-MM-DD
  const hourNum = Number(item?.open_time);
  const hh = Number.isFinite(hourNum) ? String(hourNum).padStart(2, '0') : '00';

  if (!dateStr) return '';

  const [, month, day] = dateStr.split('-');
  return `${Number(month)}월${Number(day)}일 ${hh}:00`;
}

async function postData(page = currentPage.value) {
  try {
    const response = await api.post('/goods.php', {
      mode: 'market',
      order: '',
      rows: rowsPerPage,
      page: page,
      type: 'G',
      idnt_code: idntCode.value,
    });

    if (response.data) {
      totalPages.value = response.data.page;
      const result = response.data.data.map(item => {
        return {
          ...item,
          status: _getPeriodStatus(item),
        };
      });
      products.value = result;
      prodValue.value = response?.data;
    } else {
      console.error('API 응답에 결과가 없습니다.');
    }
  } catch (error) {
    console.error('에러 발생:', error);
  }
}
</script>
